import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import CategoryDeletionModal from '../../Components/CategoryDeletionModal';
import {
    GlobalLocationCategories,
    fetchLocationCategories,
    getAllCategoryOptions,
    getCategoryOptions,
    getCategoryLabel,
    addCategory,
    updateCategoryName,
    deleteCategory,
    updateCategoryLabel,
    checkCategoryDeletion,
    LocationCategoryOption,
} from '../../Helpers/locationCategoryUtils';
import { Helmet } from 'react-helmet-async';

const LocationCategoriesManagementPage: React.FC = () => {
    const [categories, setCategories] = useState<GlobalLocationCategories | null>(null);
    const [loading, setLoading] = useState(true);
    const [expandedLevels, setExpandedLevels] = useState<Set<number>>(new Set([1]));
    const [editingItem, setEditingItem] = useState<{ level: number; key: string; currentName: string } | null>(null);
    const [editingLabel, setEditingLabel] = useState<number | null>(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryParent, setNewCategoryParent] = useState('');
    const [addingToLevel, setAddingToLevel] = useState<number | null>(null);
    
    // Deletion modal state
    const [deletionModal, setDeletionModal] = useState<{
        show: boolean;
        level: 1 | 2 | 3 | 4 | 5;
        key: string;
        name: string;
        canDelete: boolean;
        buildingUsage?: { count: number; buildingNames: string[] };
        childCategories?: { count: number; childNames: string[] };
    } | null>(null);
    
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const data = await fetchLocationCategories();
            setCategories(data);
        } catch (error) {
            console.error('Error loading categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleLevel = (level: number) => {
        const newExpanded = new Set(expandedLevels);
        if (newExpanded.has(level)) {
            newExpanded.delete(level);
        } else {
            newExpanded.add(level);
        }
        setExpandedLevels(newExpanded);
    };

    const handleAddCategory = async (level: 1 | 2 | 3 | 4 | 5) => {
        if (!newCategoryName.trim()) {
            setErrorMessage('Ange ett namn för kategorin');
            return;
        }

        if (level > 1 && !newCategoryParent) {
            setErrorMessage('Välj en överordnad kategori');
            return;
        }

        try {
            await addCategory(level, newCategoryName.trim(), level > 1 ? newCategoryParent : undefined);
            await loadCategories();
            setAddingToLevel(null);
            setNewCategoryName('');
            setNewCategoryParent('');
        } catch (error) {
            setErrorMessage('Kunde inte lägga till kategori');
            console.error(error);
        }
    };

    const handleEditCategory = async (level: 1 | 2 | 3 | 4 | 5, key: string, newName: string) => {
        if (!newName.trim()) {
            setErrorMessage('Namnet kan inte vara tomt');
            return;
        }

        try {
            await updateCategoryName(level, key, newName.trim());
            await loadCategories();
            setEditingItem(null);
        } catch (error) {
            setErrorMessage('Kunde inte uppdatera kategori');
            console.error(error);
        }
    };

    const handleDeleteCategory = async (level: 1 | 2 | 3 | 4 | 5, key: string, name: string) => {
        if (!categories) return;

        // Check if category can be deleted
        const deletionCheck = await checkCategoryDeletion(level, key, categories);

        // Show modal with deletion info
        setDeletionModal({
            show: true,
            level,
            key,
            name,
            canDelete: deletionCheck.canDelete,
            buildingUsage: deletionCheck.buildingUsage.count > 0 ? deletionCheck.buildingUsage : undefined,
            childCategories: deletionCheck.childCategories.count > 0 ? deletionCheck.childCategories : undefined,
        });
    };

    const confirmDeleteCategory = async () => {
        if (!deletionModal) return;
        
        // Safety check - should never happen but prevents accidental deletion
        if (!deletionModal.canDelete) {
            setErrorMessage('Kategorin kan inte tas bort - den används fortfarande');
            setDeletionModal(null);
            return;
        }

        try {
            await deleteCategory(deletionModal.level, deletionModal.key);
            await loadCategories();
            setDeletionModal(null);
        } catch (error) {
            setErrorMessage('Kunde inte ta bort kategori');
            console.error(error);
        }
    };

    const handleUpdateLabel = async (level: 1 | 2 | 3 | 4 | 5, newLabel: string) => {
        if (!newLabel.trim()) {
            setErrorMessage('Etiketten kan inte vara tom');
            return;
        }

        try {
            await updateCategoryLabel(level, newLabel.trim());
            await loadCategories();
            setEditingLabel(null);
        } catch (error) {
            setErrorMessage('Kunde inte uppdatera etikett');
            console.error(error);
        }
    };

    const renderCategoryLevel = (level: 1 | 2 | 3 | 4 | 5) => {
        if (!categories) return null;

        const options = getAllCategoryOptions(categories, level);
        const label = getCategoryLabel(categories, level);
        const isExpanded = expandedLevels.has(level);

        // Group by parent for levels 2-5
        const grouped: { [parent: string]: LocationCategoryOption[] } = {};
        
        if (level === 1) {
            grouped['root'] = options;
        } else {
            options.forEach(option => {
                const parentKey = option.parent || 'no-parent';
                if (!grouped[parentKey]) {
                    grouped[parentKey] = [];
                }
                grouped[parentKey].push(option);
            });
        }

        return (
            <div key={level} style={{ marginBottom: '30px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                {/* Header */}
                <div
                    style={{
                        padding: '15px 20px',
                        backgroundColor: '#f8f8f8',
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                    }}
                    onClick={() => toggleLevel(level)}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '18px' }}>{isExpanded ? '▼' : '▶'}</span>
                        {editingLabel === level ? (
                            <input
                                type="text"
                                defaultValue={label}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={(e) => handleUpdateLabel(level, e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleUpdateLabel(level, e.currentTarget.value);
                                    } else if (e.key === 'Escape') {
                                        setEditingLabel(null);
                                    }
                                }}
                                autoFocus
                                style={{
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    padding: '4px 8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                }}
                            />
                        ) : (
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                                Nivå {level}: {label}
                            </h3>
                        )}
                        <span style={{ fontSize: '14px', color: '#666' }}>({options.length} st)</span>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setEditingLabel(level)}
                            style={{
                                padding: '6px 14px',
                                fontSize: '13px',
                                backgroundColor: 'white',
                                color: '#555',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                marginRight: '8px',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f8f8';
                                e.currentTarget.style.borderColor = '#999';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.borderColor = '#ddd';
                            }}
                        >
                            Ändra etikett
                        </button>
                        <button
                            onClick={() => {
                                // Expand the section if it's not already expanded
                                if (!expandedLevels.has(level)) {
                                    const newExpanded = new Set(expandedLevels);
                                    newExpanded.add(level);
                                    setExpandedLevels(newExpanded);
                                }
                                setAddingToLevel(level);
                                setNewCategoryName('');
                                setNewCategoryParent('');
                            }}
                            style={{
                                padding: '6px 14px',
                                fontSize: '13px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#1976D2';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#2196F3';
                            }}
                        >
                            + Lägg till
                        </button>
                    </div>
                </div>

                {/* Content */}
                {isExpanded && (
                    <div style={{ padding: '20px' }}>
                        {/* Add new category form */}
                        {addingToLevel === level && (
                            <div
                                style={{
                                    marginBottom: '20px',
                                    padding: '15px',
                                    backgroundColor: '#f0f8ff',
                                    borderRadius: '4px',
                                    border: '1px solid #b0d4ff',
                                }}
                            >
                                <h4 style={{ marginTop: 0, marginBottom: '10px' }}>Lägg till ny kategori</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {level > 1 && (
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                                                Överordnad kategori:
                                            </label>
                                            <select
                                                value={newCategoryParent}
                                                onChange={(e) => setNewCategoryParent(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    fontSize: '14px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                }}
                                            >
                                                <option value="">-- Välj överordnad kategori --</option>
                                                {getAllCategoryOptions(categories, (level - 1) as 1 | 2 | 3 | 4).map(option => (
                                                    <option key={option.key} value={option.key}>
                                                        {option.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                                            Namn:
                                        </label>
                                        <input
                                            type="text"
                                            value={newCategoryName}
                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                            placeholder="Ange namn..."
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                fontSize: '14px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleAddCategory(level);
                                                }
                                            }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={() => handleAddCategory(level)}
                                            style={{
                                                padding: '8px 20px',
                                                fontSize: '14px',
                                                backgroundColor: '#2196F3',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                                transition: 'all 0.2s',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#1976D2';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#2196F3';
                                            }}
                                        >
                                            Spara
                                        </button>
                                        <button
                                            onClick={() => {
                                                setAddingToLevel(null);
                                                setNewCategoryName('');
                                                setNewCategoryParent('');
                                            }}
                                            style={{
                                                padding: '8px 20px',
                                                fontSize: '14px',
                                                backgroundColor: 'white',
                                                color: '#666',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f8f8f8';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'white';
                                            }}
                                        >
                                            Avbryt
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Category list */}
                        {options.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                                Inga kategorier tillagda ännu
                            </div>
                        ) : (
                            <div>
                                {Object.entries(grouped).map(([parentKey, childOptions]) => {
                                    if (childOptions.length === 0) return null;

                                    // Get parent name for display
                                    let parentName = '';
                                    if (level > 1 && parentKey !== 'root' && parentKey !== 'no-parent') {
                                        const parentOption = getAllCategoryOptions(categories, (level - 1) as 1 | 2 | 3 | 4).find(
                                            o => o.key === parentKey
                                        );
                                        parentName = parentOption?.name || '';
                                    }

                                    return (
                                        <div key={parentKey} style={{ marginBottom: '20px' }}>
                                            {level > 1 && parentName && (
                                                <div
                                                    style={{
                                                        fontSize: '14px',
                                                        color: '#666',
                                                        marginBottom: '8px',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    Under: {parentName}
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {childOptions.map(option => (
                                                    <div
                                                        key={option.key}
                                                        style={{
                                                            padding: '12px 16px',
                                                            backgroundColor: 'white',
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: '4px',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {editingItem?.level === level && editingItem?.key === option.key ? (
                                                            <input
                                                                type="text"
                                                                defaultValue={editingItem.currentName}
                                                                onBlur={(e) => handleEditCategory(level, option.key, e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        handleEditCategory(level, option.key, e.currentTarget.value);
                                                                    } else if (e.key === 'Escape') {
                                                                        setEditingItem(null);
                                                                    }
                                                                }}
                                                                autoFocus
                                                                style={{
                                                                    flex: 1,
                                                                    fontSize: '14px',
                                                                    padding: '4px 8px',
                                                                    border: '1px solid #ccc',
                                                                    borderRadius: '4px',
                                                                }}
                                                            />
                                                        ) : (
                                                            <span style={{ fontSize: '14px' }}>{option.name}</span>
                                                        )}
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <button
                                                                onClick={() =>
                                                                    setEditingItem({ level, key: option.key, currentName: option.name })
                                                                }
                                                                style={{
                                                                    padding: '5px 12px',
                                                                    fontSize: '12px',
                                                                    backgroundColor: 'white',
                                                                    color: '#555',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#f8f8f8';
                                                                    e.currentTarget.style.borderColor = '#2196F3';
                                                                    e.currentTarget.style.color = '#2196F3';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = 'white';
                                                                    e.currentTarget.style.borderColor = '#ddd';
                                                                    e.currentTarget.style.color = '#555';
                                                                }}
                                                            >
                                                                Redigera
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteCategory(level, option.key, option.name)}
                                                                style={{
                                                                    padding: '5px 12px',
                                                                    fontSize: '12px',
                                                                    backgroundColor: 'white',
                                                                    color: '#666',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#fff5f5';
                                                                    e.currentTarget.style.borderColor = '#f44336';
                                                                    e.currentTarget.style.color = '#f44336';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = 'white';
                                                                    e.currentTarget.style.borderColor = '#ddd';
                                                                    e.currentTarget.style.color = '#666';
                                                                }}
                                                            >
                                                                Ta bort
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>Laddar...</p>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Hantera Platskategorier - Molnfastigheter</title>
            </Helmet>
            <div>
                <h2>Hantera Platskategorier</h2>
                <p style={{ color: '#666', marginBottom: '30px' }}>
                    Här kan du skapa och hantera de platskategorier som används för att organisera byggnader. Kategorier
                    kan ha upp till 5 nivåer med hierarkiska relationer.
                </p>

                {renderCategoryLevel(1)}
                {renderCategoryLevel(2)}
                {renderCategoryLevel(3)}
                {renderCategoryLevel(4)}
                {renderCategoryLevel(5)}
            </div>

            {/* Deletion Modal */}
            {deletionModal && (
                <CategoryDeletionModal
                    show={deletionModal.show}
                    onHide={() => setDeletionModal(null)}
                    categoryName={deletionModal.name}
                    canDelete={deletionModal.canDelete}
                    buildingUsage={deletionModal.buildingUsage}
                    childCategories={deletionModal.childCategories}
                    onConfirmDelete={confirmDeleteCategory}
                />
            )}

            {/* Error Modal */}
            <Modal show={!!errorMessage} onHide={() => setErrorMessage(null)}>
                <Modal.Header closeButton style={{ backgroundColor: '#f8d7da', borderBottom: '1px solid #f5c6cb' }}>
                    <Modal.Title style={{ color: '#721c24', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>❌</span>
                        Fel
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ margin: 0, fontSize: '16px' }}>{errorMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setErrorMessage(null)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default LocationCategoriesManagementPage;

